import { useState, useMemo, useEffect, FormEvent } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useArchiveOptions, useArchiveLedger } from '@/api/hooks/useLedgers';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import type { Ledger } from '@/api/types';
import { useCurrencyFractionDigits } from '@/hooks/useCurrencyFractionDigits';
import { decimalToMinorUnits, minorUnitsToDecimal, minorUnitStep } from '@/utils/money';

interface ArchiveConfirmDialogProps {
    ledger: Ledger;
    isOpen: boolean;
    onClose: () => void;
}

/**
 * Archive confirmation dialog with conditional fields.
 *
 * Three cases:
 * 1. Ledger with value = 0 or null: Simple confirmation
 * 2. Ledger with value > 0 (no returns or debt): Date picker only
 * 3. Asset ledger with value > 0 AND has returns: Date picker + withdrawal fields
 */
export function ArchiveConfirmDialog({ ledger, isOpen, onClose }: ArchiveConfirmDialogProps) {
    const { settings } = useAuth();
    const { data: archiveOptions, isLoading: isLoadingOptions } = useArchiveOptions(ledger.id, isOpen);
    const archiveMutation = useArchiveLedger();

    const typeLabel = ledger.type === 'asset' ? 'asset' : 'debt';

    // Form state
    const [archiveDate, setArchiveDate] = useState('');
    const [withdrawalAmount, setWithdrawalAmount] = useState('');
    const [withdrawalCurrency, setWithdrawalCurrency] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const latestFractionDigits = useCurrencyFractionDigits(archiveOptions?.latestValueCurrency ?? '');
    const withdrawalFractionDigits = useCurrencyFractionDigits(withdrawalCurrency);

    // Get user's preferred currencies (following MoneyInput pattern)
    const currencyOptions = useMemo(() => {
        const currencies = settings?.currencies || ['USD'];
        const options = currencies.map((code) => ({
            value: code,
            label: code,
        }));

        // Ensure latestValueCurrency is in the list (edge case: ledger uses currency not in user's preferences)
        const latestCurrency = archiveOptions?.latestValueCurrency;
        if (latestCurrency && !currencies.includes(latestCurrency)) {
            options.unshift({ value: latestCurrency, label: latestCurrency });
        }

        return options;
    }, [settings?.currencies, archiveOptions?.latestValueCurrency]);

    // Initialize form values when options are loaded
    useEffect(() => {
        if (archiveOptions) {
            // Set default date to minArchiveDate or today
            if (archiveOptions.minArchiveDate) {
                setArchiveDate(archiveOptions.minArchiveDate);
            } else {
                const today = new Date().toISOString().split('T')[0] ?? '';
                setArchiveDate(today);
            }

            // Set default withdrawal amount to latest value (converted from minor units)
            if (archiveOptions.needsWithdrawal && archiveOptions.latestValue !== null) {
                setWithdrawalAmount(minorUnitsToDecimal(archiveOptions.latestValue, latestFractionDigits));
            }

            // Set default currency
            if (archiveOptions.latestValueCurrency) {
                setWithdrawalCurrency(archiveOptions.latestValueCurrency);
            } else if (settings?.displayCurrency) {
                setWithdrawalCurrency(settings.displayCurrency);
            } else {
                setWithdrawalCurrency('USD');
            }
        }
    }, [archiveOptions, latestFractionDigits, settings?.displayCurrency]);

    const resetForm = () => {
        setArchiveDate('');
        setWithdrawalAmount('');
        setWithdrawalCurrency('');
        setErrors({});
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setErrors({});

        if (!archiveOptions) return;

        const newErrors: Record<string, string> = {};

        // Validate date if required
        if (archiveOptions.needsArchiveDate) {
            if (!archiveDate) {
                newErrors.date = 'Archive date is required';
            } else if (archiveOptions.minArchiveDate && archiveDate < archiveOptions.minArchiveDate) {
                newErrors.date = `Date must be on or after ${archiveOptions.minArchiveDate}`;
            }
        }

        // Validate withdrawal if required
        if (archiveOptions.needsWithdrawal) {
            if (!withdrawalAmount.trim()) {
                newErrors.amount = 'Withdrawal amount is required';
            }
            if (!withdrawalCurrency) {
                newErrors.currency = 'Currency is required';
            }
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        let withdrawalValue: number | undefined;
        if (archiveOptions.needsWithdrawal) {
            try {
                withdrawalValue = decimalToMinorUnits(withdrawalAmount, withdrawalFractionDigits);
                if (withdrawalValue <= 0) {
                    throw new Error('Withdrawal amount must be greater than 0');
                }
            } catch (error) {
                setErrors({ amount: error instanceof Error ? error.message : 'Invalid withdrawal amount' });
                return;
            }
        }

        try {
            const data: { archive: boolean; archiveDate?: string; withdrawalValue?: number; withdrawalCurrency?: string } = {
                archive: true,
            };

            if (archiveOptions.needsArchiveDate && archiveDate) {
                data.archiveDate = archiveDate;
            }

            if (archiveOptions.needsWithdrawal) {
                data.withdrawalValue = withdrawalValue;
                data.withdrawalCurrency = withdrawalCurrency;
            }

            await archiveMutation.mutateAsync({ id: ledger.id, data });
            toast.success(`${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} archived`);
            onClose();
        } catch {
            toast.error(`Failed to archive ${typeLabel}`);
        }
    };

    const handleSimpleArchive = async () => {
        try {
            await archiveMutation.mutateAsync({ id: ledger.id, data: { archive: true } });
            toast.success(`${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} archived`);
            onClose();
        } catch {
            toast.error(`Failed to archive ${typeLabel}`);
        }
    };

    const isLoading = isLoadingOptions || archiveMutation.isPending;

    // Case 1: Simple confirmation (no date or withdrawal needed)
    if (archiveOptions && !archiveOptions.needsArchiveDate) {
        return (
            <Dialog
                open={isOpen}
                onClose={onClose}
                title={`Archive ${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)}`}
                size="sm"
                afterLeave={resetForm}
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Are you sure you want to archive{' '}
                        <strong className="font-semibold text-gray-900 dark:text-gray-100">
                            {ledger.label}
                        </strong>
                        ?
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        This {typeLabel} will remain in the system. You can access it at any time using the Archived filter.
                    </p>

                    <div className="flex justify-end gap-3 pt-2">
                        <Button
                            variant="secondary"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSimpleArchive}
                            isLoading={archiveMutation.isPending}
                        >
                            Archive
                        </Button>
                    </div>
                </div>
            </Dialog>
        );
    }

    // Case 2 & 3: Need date and possibly withdrawal
    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            title={`Archive ${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)}`}
            size="md"
            afterLeave={resetForm}
        >
            {isLoadingOptions ? (
                <div className="py-8 flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        The valuation of{' '}
                        <strong className="font-semibold text-gray-900 dark:text-gray-100">
                            {ledger.label}
                        </strong>{' '}
                        will be set to zero from the archiving date.
                    </p>

                    <Input
                        label="Archiving Date"
                        type="date"
                        value={archiveDate}
                        onChange={(e) => setArchiveDate(e.target.value)}
                        min={archiveOptions?.minArchiveDate ?? undefined}
                        error={errors.date}
                        required
                    />

                    {archiveOptions?.needsWithdrawal && (
                        <>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                The amount you enter will be recorded as a cash withdrawal from this {typeLabel}.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-20">
                                <Input
                                    label="Withdrawal Amount"
                                    type="number"
                                    step={minorUnitStep(withdrawalFractionDigits)}
                                    min={minorUnitStep(withdrawalFractionDigits)}
                                    value={withdrawalAmount}
                                    onChange={(e) => setWithdrawalAmount(e.target.value)}
                                    error={errors.amount}
                                    placeholder="0.00"
                                    required
                                />
                                <Select
                                    label="Currency"
                                    value={withdrawalCurrency}
                                    onChange={setWithdrawalCurrency}
                                    options={currencyOptions}
                                    error={errors.currency}
                                    maxVisibleItems={4}
                                />
                            </div>
                        </>
                    )}

                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        This {typeLabel} will remain in the system. You can access it at any time using the Archived filter.
                    </p>

                    <div className="flex justify-end gap-3 pt-2">
                        <Button
                            variant="secondary"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            isLoading={archiveMutation.isPending}
                        >
                            Archive
                        </Button>
                    </div>
                </form>
            )}
        </Dialog>
    );
}
